import {
  User,
  Project,
  ProjectDetails,
  UserDetails,
  UserFilters,
  AnalyticsOverview,
  UsersListResponse,
  ProjectsListResponse,
  CreditInfo
} from '../types/billing';

const API_BASE_URL = '/api'; // Uses proxy from package.json

// Generic API request handler
async function apiRequest<T>(endpoint: string, options?: RequestInit): Promise<T> {
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`API request error for ${endpoint}:`, error);
    throw error;
  }
}

// API functions for the frontend
const api = {
  billing: {
    // User endpoints
    getUsers: (params?: {
      page?: number;
      limit?: number;
    }): Promise<UsersListResponse> => {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      
      return apiRequest<UsersListResponse>(`/billing/users?${queryParams.toString()}`);
    },

    getUser: (userId: string, params?: { workspace_id?: number; subscription_id?: number }): Promise<UserDetails> => {
      const query = new URLSearchParams();
      if (params?.workspace_id != null) query.append('workspace_id', String(params.workspace_id));
      if (params?.subscription_id != null) query.append('subscription_id', String(params.subscription_id));
      const qs = query.toString();
      return apiRequest<UserDetails>(`/billing/users/${userId}${qs ? `?${qs}` : ''}`);
    },

    getUserFilters: (userId: string, params?: { workspace_id?: number }): Promise<UserFilters> => {
      const query = new URLSearchParams();
      if (params?.workspace_id != null) query.append('workspace_id', String(params.workspace_id));
      const qs = query.toString();
      return apiRequest<UserFilters>(`/billing/users/${userId}/filters${qs ? `?${qs}` : ''}`);
    },

    // Project endpoints
    getProjects: (params?: {
      page?: number;
      limit?: number;
      days?: number;
      user_id?: string;
      project_status?: string;
      project_id?: string;
      project_name?: string;
      sort_by?: string;
      sort_order?: string;
    }): Promise<ProjectsListResponse> => {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.days) queryParams.append('days', params.days.toString());
      if (params?.user_id) queryParams.append('user_id', params.user_id);
      if (params?.project_status) queryParams.append('project_status', params.project_status);
      if (params?.project_id) queryParams.append('project_id', params.project_id);
      if (params?.project_name) queryParams.append('project_name', params.project_name);
      if (params?.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params?.sort_order) queryParams.append('sort_order', params.sort_order);
      
      return apiRequest<ProjectsListResponse>(`/billing/projects/list?${queryParams.toString()}`);
    },

    getProject: (projectId: string, days: number = 30): Promise<ProjectDetails> => {
      return apiRequest<ProjectDetails>(`/billing/projects/${projectId}?days=${days}`);
    },

    // Credit endpoints
    getCredits: (userId: string): Promise<CreditInfo> => {
      return apiRequest<CreditInfo>(`/billing/credits/${userId}`);
    },

    // Health check
    getHealth: (): Promise<{ status: string; service: string; timestamp: string; version: string }> => {
      return apiRequest('/billing/health');
    }
  },

  analytics: {
    getOverview: (): Promise<AnalyticsOverview> => {
      return apiRequest<AnalyticsOverview>('/analytics/overview');
    }
  },

  // Legacy support for old project endpoints
  projects: {
    list: (): Promise<Project[]> => {
      return api.billing.getProjects().then(response => response.projects);
    },
    
    get: (id: string): Promise<Project> => {
      return api.billing.getProject(id).then(response => response.project as any);
    },

    getRealtime: (id: string): Promise<any> => {
      // This would need to be mapped to the new billing endpoints
      return api.billing.getProject(id);
    }
  },

  // Legacy support for monitoring
  monitoring: {
    getHealth: () => {
      return api.billing.getHealth();
    }
  }
};

export default api;